import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { BaseButton } from 'components/ui/buttons';
import { AlertIcon, LeftIcon, PlayIcon, PrintIcon } from 'assets/icons';
import { AppRoutes, replaceRouteParamWithValue } from 'routes/app-routes';
import { QRTemplatesModal } from 'components/details-pages/modals/QRTamplatesModal';
import { MessageModal } from 'components/ui/modal/MessageModal';
import dayjs from 'dayjs';

type PatientAddedSuccessProps = {
  patientId: string | null;
  patientRefId: number;
  patientDatetimeOfBirth: string;
};

export const PatientAddedSuccess: React.FC<PatientAddedSuccessProps> = ({
  patientId,
  patientRefId,
  patientDatetimeOfBirth,
}) => {
  const [isShowQRTemplateModal, setShowQRTemplateModal] = useState(false);
  const [isShowInfantTooYoungModal, setIsShowInfantTooYoungModal] = useState(false);
  const navigate = useNavigate();

  const handleStartAssessment = () => {
    if (!patientId) return;

    const currentDate = dayjs();
    const patientDate = dayjs(patientDatetimeOfBirth);
    const diffInHours = currentDate.diff(patientDate, 'hour', true);
    if (diffInHours < 4) {
      setIsShowInfantTooYoungModal(true);
      return;
    }

    const route = replaceRouteParamWithValue(AppRoutes.Assessment, 'id', patientId);
    navigate(route);
  };

  const handleBackToRoster = () => {
    navigate(AppRoutes.Roster);
  };

  const handlePrintQRCode = () => {
    setShowQRTemplateModal(true);
  };

  return (
    <div className="p-10 min-h-[calc(100vh-72px)]">
      <div className="bg-add-patient-success pt-[385px] bg-center bg-top bg-no-repeat">
        <h1 className="max-w-[420px] mx-auto self-stretch text-gray-900 dark:text-silver-400 text-center text-[28px] font-semibold leading-[normal]">
          You have successfully added patient ID: {patientRefId}
        </h1>
        <p className="max-w-[520px] mx-auto mt-3 mb-12 self-stretch text-gray-500 dark:text-silver-400 text-center text-base font-medium leading-[150%]">
          Print your patient's QR code or begin your first assessment.
          <br />
          NOTE: Begin assessments once the patient is clinically stable post-delivery and at least 4 hours old.
        </p>
        <div>
          <BaseButton
            scheme="cta-primary"
            className="w-full mb-4"
            size="xl"
            icon={PlayIcon}
            isIconOnLeftSide
            onClick={handleStartAssessment}
          >
            Start First Assessment
          </BaseButton>
          <div className="flex gap-6 w-full pt-4">
            <BaseButton
              scheme="cta-secondary"
              className="w-full"
              size="xl"
              icon={LeftIcon}
              isIconOnLeftSide
              onClick={handleBackToRoster}
            >
              Back to Roster
            </BaseButton>
            <BaseButton
              scheme="cta-secondary"
              className="w-full"
              size="xl"
              icon={PrintIcon}
              isIconOnLeftSide
              onClick={handlePrintQRCode}
            >
              Print QR Code
            </BaseButton>
          </div>
        </div>
      </div>

      {isShowQRTemplateModal && patientId && (
        <QRTemplatesModal
          refId={patientRefId}
          onCancelClick={() => {
            setShowQRTemplateModal(false);
          }}
          patientId={patientId}
        />
      )}

      {isShowInfantTooYoungModal && (
        <MessageModal
          icon={AlertIcon}
          iconProps={{ className: 'text-silver-400' }}
          successText="Ok"
          title="Infant Too Young for Assessment"
          description="The first assessment can only be performed when the infant is at least 4 hours old and fully transitioned. Please wait until the recommended time before starting the assessment."
          onCancelClick={() => setIsShowInfantTooYoungModal(false)}
          onSuccessClick={() => {
            setIsShowInfantTooYoungModal(false);
          }}
          isShownCloseButton={false}
        />
      )}
    </div>
  );
};
